import { Input, Button, Header, Container, SpaceBetween } from '@amzn/awsui-components-console';
import moment from 'moment-timezone';
import { Fragment } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

import { useGetClassroom, useGetLearningActivity } from '@/api/hooks';
import {
    Loader,
    AccessCodeTable,
    SurveyLinkWidget,
    GilmoreHealthBanner,
    JamDetailsSection,
    WidgetContainer,
    DeliverySessionList,
    ClassPerformanceSection,
} from '@/components';
import { AttendanceTable } from '@/components/AttendanceTable';
import { CLASS_ACCESS_TYPES, LOCATION_TYPES } from '@/components/classForm/ClassForm.utils';
import { ClassRosterTable } from '@/components/ClassRosterTable';
import { useProvider } from '@/data/ProviderContext';
import { paths } from '@/routing/paths';
import { getStudentURL, getSurveyURL, useFlags } from '@/utils';

import messages from './ClassDetailPage.messages';

import './ClassDetailPage.scss';

const ClassDetailPage = () => {
    const { formatMessage } = useIntl();
    const { classroomId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const provider = useProvider();
    const { supportsActivities } = useFlags();
    const { data, isLoading, refetch: classroomRefetch } = useGetClassroom();
    const { data: learningActivityResponse } = useGetLearningActivity();
    const learningActivity = learningActivityResponse?.getLearningActivity;

    const { classroom: classData = {}, jamTrainings = {} } = data?.getClassroom ?? {};
    const jams = jamTrainings.trainings ?? [];

    const { providerArn, accessType, accessCodes = [], locationData = {} } = classData;
    const {
        virtualUrl,
        locationType,
        timezone = moment.tz.guess(),
        physicalAddress,
    } = locationData;

    const { addressLine1, addressLine2, city, state, postalCode, country } = physicalAddress || {};

    const hasStudentRoster = accessType === CLASS_ACCESS_TYPES.roster;

    if (providerArn && provider.arn && provider.arn !== providerArn) {
        // The selected provider doesn't match the provider for this classroom
        // Redirect the user to the provider select page and auto-select the provider of the classroom
        navigate(
            {
                pathname: paths.providersPage,
                search: `?${new URLSearchParams({ providerArn }).toString()}`,
            },
            {
                state: { referrer: location.pathname },
            },
        );
        return <Loader isLoading={true} />;
    }

    return (
        <Loader isLoading={isLoading}>
            <div data-testid='class-detail-page'>
                <GilmoreHealthBanner />
                <SpaceBetween direction='vertical' size='l'>
                    {learningActivity?.lms_type === 'myClass' && (
                        <ClassPerformanceSection
                            csatData={learningActivity?.csat}
                            classAttendance={learningActivity?.attendedCounter}
                        />
                    )}
                    <Container
                        header={
                            <Header variant='h2'>
                                {formatMessage(messages.classLocationHeader)}
                            </Header>
                        }
                    >
                        {locationType === LOCATION_TYPES.virtual ? (
                            <Fragment>
                                <WidgetContainer
                                    title={
                                        <span id='class-detail__virtual-url-label'>
                                            {formatMessage(messages.virtualClassUrlLabel)}
                                        </span>
                                    }
                                >
                                    <div className='class-detail__virtual-url-icon-wrapper'>
                                        <Button
                                            variant='icon'
                                            iconName='external'
                                            ariaLabel={formatMessage(
                                                messages.openVirtualClassUrlLabel,
                                            )}
                                            href={virtualUrl}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                        />
                                    </div>

                                    <Input // using input for easier to copy text
                                        label={formatMessage(messages.virtualClassUrlLabel)}
                                        ariaRequired
                                        readOnly
                                        ariaLabelledby='class-detail__virtual-url-label'
                                        value={virtualUrl}
                                    />
                                </WidgetContainer>
                            </Fragment>
                        ) : null}
                        {locationType === LOCATION_TYPES.physical ? (
                            <WidgetContainer title={formatMessage(messages.classAddressLabel)}>
                                <div>{addressLine1}</div>
                                <div>{addressLine2}</div>
                                <>
                                    {city}, {state} {postalCode}, {country}
                                </>
                            </WidgetContainer>
                        ) : null}
                    </Container>

                    {learningActivity?.lms_type === 'CustomerLMS' && (
                        <SurveyLinkWidget surveyURL={getSurveyURL(learningActivity?.pk)} />
                    )}

                    <AccessCodeTable
                        accessCodes={accessCodes}
                        studentUrl={getStudentURL(classroomId)}
                        hasStudentRoster={hasStudentRoster}
                    />

                    {learningActivity?.delivery_sessions?.length > 0 && (
                        <DeliverySessionList
                            sessions={learningActivity.delivery_sessions}
                            timezone={timezone}
                        />
                    )}

                    {learningActivity && supportsActivities ? (
                        <AttendanceTable />
                    ) : !supportsActivities ? (
                        <ClassRosterTable />
                    ) : null}
                    <JamDetailsSection jamTrainings={jams} classroomRefetch={classroomRefetch} />
                </SpaceBetween>
            </div>
        </Loader>
    );
};

export default ClassDetailPage;
